/*
 * Main component for the control charts
 * It contains the carrousels for the ECL and the ECL gaps
 * It then displays several charts with the data received from the API.
 */

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "hooks/useAuth";
import { horusApi } from "services/api/horus";
import { Row, Col, Carousel } from "react-bootstrap";
import { SelectBox } from "devextreme-react/select-box";
import { getClientPalette, defaultPalette } from "config/clientPalettes";
import { useGroupedItems } from "hooks/useGroupedItems";
import { HORUS_AMORTIZATION_TYPE } from "data/ENUM/add-options";
import EmptyCard from "components/common/pyramid/EmptyCard";
import NumberCard from "components/common/pyramid/NumberCard";
import PyramidCard from "components/common/pyramid/PyramidCard";
import AmortizationGapBySecurity from "./AmortizationGapBySecurity";
import AmortizationGapByInstrumentType from "./AmortizationGapByInstrumentType";
import AmortizationGapByAccountingMethod from "./AmortizationGapByAccountingMethod";
import AmortizationGapByInterestType from "./AmortizationGapByInterestType";

/**
 * CarouselSection Component
 * @param {Object} sumAmortizationTotal - Amortization total
 * @param {Object} sumAmortizationTotalInv - Amortization total from static data (USER)
 * @param {Object} amortizationGapTotal - Amortization gap total
 * @param {Object} amortizationGapTotalAbs - Amortization gap total absolute
 */
const CarouselSection = ({
  sumAmortizationTotal,
  sumAmortizationTotalInv,
  amortizationGapTotal,
  amortizationGapTotalAbs,
}) => {
  const carouselSettings = {
    variant: "dark",
    interval: 5000,
    controls: true,
    indicators: false,
  };

  return (
    <>
      {/* First Carousel - Shows Amortization data (HORUS and USER) */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel {...carouselSettings}>
            <Carousel.Item>
              <EmptyCard chartData={sumAmortizationTotal} />
            </Carousel.Item>
            <Carousel.Item>
              <EmptyCard chartData={sumAmortizationTotalInv} />
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>

      {/* Second Carousel - Shows Amortization Gap data (Total and Absolute) */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel {...carouselSettings}>
            <Carousel.Item>
              <div className="h-100">
                <EmptyCard chartData={amortizationGapTotal} />
              </div>
            </Carousel.Item>
            <Carousel.Item>
              <div className="h-100">
                <EmptyCard chartData={amortizationGapTotalAbs} />
              </div>
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>
    </>
  );
};

/**
 * ControlChartsHorus Component
 *
 * Main dashboard component for displaying Amortization control charts and comparisons.
 * Shows the difference (gap) between Amortization values between HORUS and USER.
 * Displays several charts with the data received from the API.
 */
const ControlChartsHorus = () => {
  // State management for chart data and UI controls
  const [chartData, setChartData] = useState({
    sumAmortizationTotal: null,
    sumAmortizationTotalInv: null,
    amortizationGapTotal: null,
    amortizationGapTotalAbs: null,
    amortizationGapBySecurity: null,
    amortizationGapByInstrumentType: null,
    amortizationGapByAccountingMethod: null,
    amortizationGapByInterestType: null,
    nbValuesAboveLimitActuarial: null,
    nbValuesAboveLimitLinear: null,
    nbValuesAboveLimitConstant: null,
  });
  const [rawChartData, setRawChartData] = useState([]); // Store all raw data from API
  const [computations, setComputations] = useState([]);
  const [selectedComputation, setSelectedComputation] = useState(null);
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAmortizationType, setSelectedAmortizationType] = useState(
    HORUS_AMORTIZATION_TYPE[3].id
  );

  // Authentication and API access
  const { accessToken, account, companyList, userList } = useAuth();
  const api = horusApi();

  // Grouping computations by user
  const groupedComputations = useGroupedItems(
    computations,
    userList,
    companyList
  );

  // Get the group display name for the selected computation
  const selectedComputationItem = computations.find(
    (item) => item.id === selectedComputation
  );
  const selectedGroupDisplayName =
    selectedComputationItem && companyList && companyList.length > 0
      ? companyList.find(
        (company) => company.id === selectedComputationItem.groupId
      )?.displayName
      : null;

  /**
   * Helper function to get chart name based on base name and amortization type
   * For other types that are not "All", appends the type as suffix (e.g., "Linear", "Actuarial", "Constant")
   */
  const getChartName = (baseName, amortizationType) => {
    if (amortizationType === "All") {
      return baseName;
    }
    return `${baseName}${amortizationType}`;
  };

  /**
   * Parse raw chart data based on selected amortization type
   * Filters and transforms data from rawChartData without making API calls
   */
  const parseChartDataByType = (rawData, amortizationType) => {
    if (!rawData || rawData.length === 0) {
      return {
        sumAmortizationTotal: null,
        sumAmortizationTotalInv: null,
        amortizationGapTotal: null,
        amortizationGapTotalAbs: null,
        amortizationGapBySecurity: [],
        amortizationGapByInstrumentType: [],
        amortizationGapByAccountingMethod: [],
        amortizationGapByInterestType: [],
        nbValuesAboveLimitActuarial: null,
        nbValuesAboveLimitLinear: null,
        nbValuesAboveLimitConstant: null,
      };
    }

    // Get chart names for the cards shown in EmptyCard / Carousel
    const sumAmortizationHorName =
      amortizationType === "All"
        ? "SumAmortizationTotal"
        : `SumAmortization${amortizationType}`;
    const sumAmortizationUserName =
      amortizationType === "All"
        ? "SumAmortizationTotalInv"
        : `SumAmortizationTotalInv${amortizationType}`;
    const amortizationGapTotalName =
      amortizationType === "All"
        ? "AmortizationGapTotal"
        : `AmortizationGapTotal${amortizationType}`;
    const amortizationGapTotalAbsName =
      amortizationType === "All"
        ? "AmortizationGapTotalAbs"
        : `AmortizationGapTotalAbs${amortizationType}`;

    // Get chart names based on selected amortization type
    const securityChartName = getChartName(
      "AmortizationGapBySecurity",
      amortizationType
    );
    const instrumentTypeChartName = getChartName(
      "AmortizationGapByInstrumentType",
      amortizationType
    );
    const interestTypeChartName = getChartName(
      "AmortizationGapByInterestType",
      amortizationType
    );

    return {
      sumAmortizationTotal:
        rawData.find((chart) => chart.Name === sumAmortizationHorName)
          ?.Data[0] || null,
      sumAmortizationTotalInv:
        rawData.find((chart) => chart.Name === sumAmortizationUserName)
          ?.Data[0] || null,
      amortizationGapTotal:
        rawData.find((chart) => chart.Name === amortizationGapTotalName)
          ?.Data[0] || null,
      amortizationGapTotalAbs:
        rawData.find((chart) => chart.Name === amortizationGapTotalAbsName)
          ?.Data[0] || null,

      amortizationGapBySecurity:
        rawData
          .find((chart) => chart.Name === securityChartName)
          ?.Data.map((item) => ({
            securityLib: item.SecurityLib,
            amortizationGapTotal: item.AmortizationGapTotal,
            isin: item.Isin,
            gapTotal: item.GapTotal,
            pctGap: item.PctGap,
            cumPctGap: item.CumPctGap,
          })) || [],

      amortizationGapByInstrumentType:
        rawData
          .find((chart) => chart.Name === instrumentTypeChartName)
          ?.Data.map((item) => ({
            instrumentType: item.InstrumentType,
            amortizationGapTotal: item.AmortizationGapTotal,
          })) || [],

      amortizationGapByAccountingMethod:
        rawData
          .find((chart) => chart.Name === "AmortizationGapByAccountingMethod")
          ?.Data.map((item) => ({
            accountingMethod: item.AccountingMethod,
            amortizationGapTotal: item.AmortizationGapTotal,
          })) || [],

      amortizationGapByInterestType:
        rawData
          .find((chart) => chart.Name === interestTypeChartName)
          ?.Data.map((item) => ({
            interestType: item.InterestType,
            amortizationGapTotal: item.AmortizationGapTotal,
          })) || [],

      nbValuesAboveLimitActuarial: (() => {
        const data = rawData.find(
          (chart) => chart.Name === "NbValuesAboveLimitActuarial"
        )?.Data[0];
        if (!data) return null;
        const [line1, line2] = data.Title.split('\n');
        return {
          Title: line1,
          Subtitle: line2,
          Amount: data.NbValues,
        };
      })(),

      nbValuesAboveLimitLinear: (() => {
        const data = rawData.find(
          (chart) => chart.Name === "NbValuesAboveLimitLinear"
        )?.Data[0];
        if (!data) return null;
        const [line1, line2] = data.Title.split('\n');
        return {
          Title: line1,
          Subtitle: line2,
          Amount: data.NbValues,
        };
      })(),

      nbValuesAboveLimitConstant: (() => {
        const data = rawData.find(
          (chart) => chart.Name === "NbValuesAboveLimitConstant"
        )?.Data[0];
        if (!data) return null;
        const [line1, line2] = data.Title.split('\n');
        return {
          Title: line1,
          Subtitle: line2,
          Amount: data.NbValues,
        };
      })(),
    };
  };

  /**
   * Effect to update the color palette when the user account changes
   * Maintains consistent branding per client
   */
  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  /**
   * Effect to fetch available computations when component mounts
   * Retrieves only completed/ready computations and sorts by creation date
   */
  useEffect(() => {
    const fetchComputations = async () => {
      try {
        if (!accessToken) {
          return;
        }

        const data = await api.getComputations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const computations = Array.isArray(data) ? data : data.result || [];
          const sortedComputations = [...computations].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setComputations(sortedComputations);

          if (sortedComputations.length > 0) {
            setSelectedComputation(sortedComputations[0].id);
          }
        } else {
          console.error("Unexpected API response format:", data);
        }
      } catch (error) {
        console.error("Error in the response:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComputations();
  }, [accessToken]);

  /**
   * Effect to fetch raw control chart data when the selected computation changes
   * Stores all data from API without filtering by amortization type
   */
  useEffect(() => {
    const fetchControlCharts = async () => {
      if (!selectedComputation || !accessToken) return;

      try {
        const data = await api.getControlCharts(selectedComputation);

        if (data) {
          const result =
            Array.isArray(data) && data.length > 0 ? data[0].Items : [];
          // Store all raw data - no filtering at this stage
          setRawChartData(result);
        }
      } catch (error) {
        console.error("Error in the response:", error);
        setRawChartData([]);
      }
    };

    fetchControlCharts();
  }, [selectedComputation, accessToken]);

  /**
   * Effect to parse and filter chart data when amortization type changes
   * Uses already-fetched rawChartData, no API call needed
   */
  useEffect(() => {
    const parsedData = parseChartDataByType(
      rawChartData,
      selectedAmortizationType
    );
    setChartData(parsedData);
  }, [rawChartData, selectedAmortizationType]);

  /* Function used to format the title (adds Amortization type if needed for ChartCards titles)*/
  const getFormattedChartTitle = (baseTitle, amortizationType) => {
    if (amortizationType === "All") {
      return baseTitle;
    }
    return `${baseTitle} (${amortizationType})`;
  };

  /**
   * Component render - Multi-section dashboard layout with various ECL gap visualizations
   * Organized in a responsive grid system using Bootstrap Row/Col components
   */
  return (
    <div>
      {/* Header Row - Computation Selector and Summary Carousels */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <PyramidCard className="h-100">
            <div className="d-flex flex-column h-100 py-2 justify-content-around">
              <div className="fw-medium fs-7 text-nowrap mt-n2">
                Computation
              </div>
              <SelectBox
                dataSource={groupedComputations}
                displayExpr="description"
                valueExpr="id"
                grouped={true}
                value={selectedComputation}
                onValueChanged={(e) => setSelectedComputation(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>

        {/* ECL and Gap Carousels Section */}
        <CarouselSection
          sumAmortizationTotal={chartData.sumAmortizationTotal}
          amortizationGapTotal={chartData.amortizationGapTotal}
          amortizationGapTotalAbs={chartData.amortizationGapTotalAbs}
          sumAmortizationTotalInv={
            chartData.sumAmortizationTotalInv &&
              (selectedGroupDisplayName || companyList.length > 0)
              ? {
                ...chartData.sumAmortizationTotalInv,
                Title: `Amortization ${selectedAmortizationType === "All"
                    ? ""
                    : `${selectedAmortizationType}`
                  } - ${selectedGroupDisplayName || companyList[0].displayName
                  }`,
              }
              : chartData.eclGroupData
          }
        />
      </Row>

      {/* Filtered Charts Section */}
      <div className="mb-4">
        {/* Section Header with Filter */}
        <div className="mb-3">
          <PyramidCard className="border-start border-primary border-3">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-3 gap-2">
              <div className="p-3 d-flex align-items-center">
                <span
                  className="badge bg-primary me-2"
                  style={{ paddingTop: "0.5rem" }}
                >
                  Filtered
                </span>
                <span className="text-muted fs-7">
                  Charts filtered by amortization type
                </span>
              </div>
              <div className="d-flex align-items-center flex-shrink-0">
                <label className="text-nowrap me-2 fw-medium fs-7 mb-0">
                  Amortization type:
                </label>
                <SelectBox
                  dataSource={HORUS_AMORTIZATION_TYPE}
                  displayExpr="name"
                  valueExpr="id"
                  value={selectedAmortizationType}
                  onValueChanged={(e) => setSelectedAmortizationType(e.value)}
                  width={180}
                  className="dx-font"
                />
              </div>
            </div>
          </PyramidCard>
        </div>

        {/* Filtered Charts Grid - Only show charts that have data for selected type */}
        {chartData.amortizationGapBySecurity &&
          chartData.amortizationGapBySecurity.length > 0 && (
            <Row className="g-3">
              <Col xs={12}>
                <AmortizationGapBySecurity
                  data={chartData.amortizationGapBySecurity}
                  palette={currentPalette}
                  title={getFormattedChartTitle(
                    "Amortization Gap by Security",
                    selectedAmortizationType
                  )}
                />
              </Col>
            </Row>
          )}

        {(chartData.amortizationGapByInstrumentType &&
          chartData.amortizationGapByInstrumentType.length > 0) ||
          (chartData.amortizationGapByInterestType &&
            chartData.amortizationGapByInterestType.length > 0) ? (
          <Row
            className="g-3"
            style={{
              marginTop:
                chartData.amortizationGapBySecurity &&
                  chartData.amortizationGapBySecurity.length > 0
                  ? "1rem"
                  : "0",
            }}
          >
            {chartData.amortizationGapByInstrumentType &&
              chartData.amortizationGapByInstrumentType.length > 0 && (
                <Col
                  md={
                    chartData.amortizationGapByInterestType &&
                      chartData.amortizationGapByInterestType.length > 0
                      ? 6
                      : 12
                  }
                >
                  <AmortizationGapByInstrumentType
                    data={chartData.amortizationGapByInstrumentType}
                    palette={currentPalette}
                    title={getFormattedChartTitle(
                      "Amortization Gap by Instrument Type",
                      selectedAmortizationType
                    )}
                  />
                </Col>
              )}
            {chartData.amortizationGapByInterestType &&
              chartData.amortizationGapByInterestType.length > 0 && (
                <Col
                  md={
                    chartData.amortizationGapByInstrumentType &&
                      chartData.amortizationGapByInstrumentType.length > 0
                      ? 6
                      : 12
                  }
                >
                  <AmortizationGapByInterestType
                    data={chartData.amortizationGapByInterestType}
                    palette={currentPalette}
                    title={getFormattedChartTitle(
                      "Amortization Gap by Interest Type",
                      selectedAmortizationType
                    )}
                  />
                </Col>
              )}
          </Row>
        ) : null}
      </div>

      {/* Non-Filtered Charts Section */}
      <div className="mb-3">
        {/* Section Header */}
        <div className="mb-3">
          <PyramidCard className="border-start border-secondary border-3">
            <div className="p-3 d-flex align-items-center">
              <span
                className="badge bg-secondary me-2"
                style={{ paddingTop: "0.5rem" }}
              >
                Unfiltered
              </span>
              <span className="text-muted fs-7">
                Charts independent of amortization type
              </span>
            </div>
          </PyramidCard>
        </div>

        {/* Non-Filtered Charts Grid - NumberCards on left, AccountingMethod on right */}
        <Row className="g-3">
          <Col md={6}>
            <Row className="g-3">
              <Col xs={12}>
                <PyramidCard className="h-100">
                  <NumberCard
                    chartData={chartData.nbValuesAboveLimitActuarial}
                  />
                </PyramidCard>
              </Col>
              <Col xs={12}>
                <PyramidCard className="h-100">
                  <NumberCard chartData={chartData.nbValuesAboveLimitLinear} />
                </PyramidCard>
              </Col>
              <Col xs={12}>
                <PyramidCard className="h-100">
                  <NumberCard
                    chartData={chartData.nbValuesAboveLimitConstant}
                  />
                </PyramidCard>
              </Col>
            </Row>
          </Col>
          <Col md={6}>
            <AmortizationGapByAccountingMethod
              data={chartData.amortizationGapByAccountingMethod}
              palette={currentPalette}
              title={getFormattedChartTitle(
                "Amortization Gap by Accounting Method",
                selectedAmortizationType
              )}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ControlChartsHorus;
